/**
 * This file contains slimmer versions of price computations, avoiding the full
 * dbShop.json and bigjs libraries. It is not intended to be used in non-11ty
 * builds since it mostly duplicates things in StoreUtil.
 */

const PriceData = require("../../../../shared/data/dbPrices.json");

// Rounds a price to the nearest cent. Used in place of bigjs prices since all
// computations here are simple and don't have anything ugly like division.
const finishPrice = function(price) {
	return Math.round(price * 100) / 100;
};

const isBookItem = function(item) {
	return PriceData.types[item.sku] === "book";
};

const isSubscriptionItem = function(item) {
	return PriceData.types[item.sku] === "subscription";
};

const isBulkCodeItem = function(item) {
	return PriceData.types[item.sku] === "code";
};

// Given a user entered quantity, change it to a valid one.
const fixQuantity = function(quantity) {
	if (!Number.isFinite(quantity) || quantity < 0) {
		return 0;
	} else if (quantity > 99) {
		return 99;
	} else {
		return Math.round(quantity);
	}
};

// Checks if an item gets a discount when multiple are purchased.
const isSubscriptionWithMultiPrice = function(item) {
	const price = PriceData.prices[item.sku];
	const multiPrice = PriceData.multiPrices[item.sku];
	const isBundle = !!(item.options && item.options.bundleID);
	return multiPrice && multiPrice < price && !isBundle;
};

// Internal helper to calculateItemPrices. Returns a price. Accepts the same
// options as calculateItemPrices and these additional ones:
// - multiSub: If true, apply the multiple subscription discount.
const calculateOneItemPrice = function(item, options = {}) {
	const {skipDiscounts = false, multiSub = false} = options;

	const {sku, quantity, options: itemOptions = {}} = item;
	let basePrice = PriceData.prices[sku];

	if (skipDiscounts && PriceData.fullPrices && PriceData.fullPrices[sku]) {
		basePrice = PriceData.fullPrices[sku];
	}
	if (
		itemOptions &&
		itemOptions.bundleID &&
		PriceData.subscriptionBundlePrices[sku]
	) {
		basePrice = PriceData.subscriptionBundlePrices[sku];
	}

	let totalPrice = basePrice * quantity;
	if ((isBookItem(item) || isBulkCodeItem(item)) && !skipDiscounts) {
		let basePriceWithDiscounts = basePrice;
		if (quantity >= 10) {
			basePriceWithDiscounts = Math.floor(basePrice * 80) / 100;
			totalPrice = basePriceWithDiscounts * quantity;
		} else if (quantity >= 5) {
			basePriceWithDiscounts = Math.floor(basePrice * 90) / 100;
			totalPrice = basePriceWithDiscounts * quantity;
		}
	} else if (isSubscriptionWithMultiPrice(item) && !skipDiscounts) {
		const multiPrice = PriceData.multiPrices[item.sku];
		totalPrice = multiSub ? multiPrice : basePrice;
		if (item.quantity > 1) {
			totalPrice += multiPrice * (item.quantity - 1);
		}
	}

	return finishPrice(totalPrice);
};

// Returns the same cartItems array except with a price field on each item.
// Options:
// - skipDiscounts: If true, ignore discounts for buying books in bulk.
const calculateItemPrices = function(cartItems, options) {
	const optionsWithMulti = Object.assign({}, options, {multiSub: true});
	const seenSubSKUs = {};
	// Bundled subscriptions with multiprice discounts don't get discounted, so
	// if there are any, preemptively set that sku as seen.
	cartItems.forEach(i => {
		if (i.options && i.options.bundleID && isSubscriptionItem(i)) {
			seenSubSKUs[i.sku] = true;
		}
	});
	return cartItems.map(i => {
		let optionsForThisItem = options;
		if (isSubscriptionWithMultiPrice(i)) {
			if (seenSubSKUs[i.sku]) {
				optionsForThisItem = optionsWithMulti;
			} else {
				seenSubSKUs[i.sku] = true;
			}
		}
		const price = calculateOneItemPrice(i, optionsForThisItem);
		return Object.assign({}, i, {price});
	});
};

// Takes the same parameters as calculateItemPrices, but instead returns a
// single number giving the total price of all items.
const calculateTotalItemPrice = function(cartItems, options) {
	const cartWithPrices = calculateItemPrices(cartItems, options);
	const total = cartWithPrices.reduce((acc, item) => acc + item.price, 0);
	return finishPrice(total);
};

const getBaseItemPriceForSku = function(sku) {
	// weird bug where the skus generated in PriceData have shirt-beast instead of beast-shirt
	return PriceData.prices[sku];
};

const getMultiPriceForSku = function(sku) {
	return PriceData.multiPrices[sku];
};

const getBundlePriceForSku = function(sku) {
	return PriceData.subscriptionBundlePrices[sku];
};

const getBundlePriceForGrade = function(grade, undiscounted) {
	const skus = [];
	if (grade === 1) {
		["a", "b", "c", "d"].map(letter => {
			skus.push("beast-1" + letter);
		});
	} else {
		["a", "b", "c", "d"].map(letter => {
			skus.push("beast-" + grade + letter);
			skus.push("beast-" + grade + letter + "-practice");
		});
	}
	skus.push("beast-yearly");

	let totalPrice = 0;
	skus.map(sku => {
		totalPrice += undiscounted
			? getBaseItemPriceForSku(sku)
			: getBundlePriceForSku(sku);
	});
	return totalPrice;
};

const getGuidePracticeBookSetPriceForGrade = function(grade) {
	const skus = [];
	["a", "b", "c", "d"].map(letter => {
		skus.push("beast-" + grade + letter);
		skus.push("beast-" + grade + letter + "-practice");
	});
	let totalPrice = 0;
	skus.map(sku => {
		totalPrice += getBaseItemPriceForSku(sku);
	});
	return totalPrice;
};

const getTypeForSku = function(sku) {
	return PriceData.types[sku];
};

module.exports = {
	finishPrice,
	fixQuantity,
	calculateItemPrices,
	calculateTotalItemPrice,
	getBaseItemPriceForSku,
	getMultiPriceForSku,
	getBundlePriceForSku,
	getBundlePriceForGrade,
	getGuidePracticeBookSetPriceForGrade,
	getTypeForSku,
};
